// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { Command } from '@atlaskit/editor-common/types';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { MarkType, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection, Transaction } from '@atlaskit/editor-prosemirror/state';
import { AllSelection, NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findParentNode, findParentNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { isFedRamp } from '@atlassian/atl-context';

import type { AIGlobalOptIn } from '../../types/types';
import {
	configItemUsesRovo,
	configItemUsesFreeGen,
} from '../../ui/components/SessionUI/utils/checkMigrationStatus';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';
import {
	isConfluenceTitleToolbarSuggestTitle,
	isSuggestTitleForConfluence,
} from '../../ui/prebuilt/config-items/utils/suggest-title';
import { getSupportedNodesForAIModal, isCellSelection } from '../../ui/utils/ai-button';
import { convertExperienceName } from '../../ui/utils/analytics/analyticsFlow';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../ui/utils/analytics/analyticsFlowTypes';
import { addAnalytics } from '../../ui/utils/analytics/utils';
import type { Positions } from '../../ui/utils/xstate/get-ai-experience-service';
import { endWatchingAnnotationChanges } from '../ai-annotation-watchmen/transforms';
import type { AdjustSelectionInstruction } from '../ai-event-hub/types';
import { aiSessionContextPluginKey } from '../ai-session-context/ai-session-context-plugin-key';
import type { PostStreamReviewUndoneUpdate } from '../ai-session-context/types';
import { endStream } from '../in-editor-direct-streaming/transforms';
import { expandSelectionForAIExperience } from '../in-editor-direct-streaming/utils/expand-positions';
import { endStream as purePmEndStream } from '../pure-pm-in-editor-direct-streaming/transforms';
import { selectionPluginKey } from '../selection/selection-plugin-key';

import { createCommand, getPluginState } from './decoration-plugin-factory';
import type { AgentInfo, ShowModalAction } from './reducer';

export type DecorationPositions = { endPos: number; startPos: number };

export const createWidgetDecoration =
	(isInDocumentStreamingEnabled?: boolean) =>
	(pos: number, type: 'start' | 'end', key?: string): Decoration => {
		const widgetSpanElement = document.createElement('span');
		// Firefox needs content inside the span in order to add height to the element.
		// Otherwise, the height is set to 0 which prevents the start and
		// end widgets from rendering correctly and produces incorrect measurements
		// when we need to determine where to place the modal under the content.
		// Zero width spaces have been known to introduce some strange selection
		// behaviour in the Editor, however this approach works for our use case
		// in the ai-plugin where we do not expect users to be making selections
		// over ai-highlighted content in the same way they select regular content
		// in the Editor, this is also not intended to be a permanent fix post EAP.
		if (!isInDocumentStreamingEnabled) {
			widgetSpanElement.appendChild(document.createTextNode('\u200b'));
		}
		widgetSpanElement.setAttribute('data-testid', `ai-modal-${type}-widget`);
		widgetSpanElement.className = `ai-modal-${type}-widget`;

		widgetSpanElement.contentEditable = 'false';

		return Decoration.widget(pos, widgetSpanElement, {
			side: type === 'start' ? 1 : -1,
			key: key ?? `${type}WidgetDecoration`,
		});
	};

export const createModalMountPointDecoration = (pos: number): Decoration => {
	const widgetSpanElement = document.createElement('span');
	widgetSpanElement.setAttribute('data-testid', `ai-modal-mount-point-widget`);
	widgetSpanElement.className = `ai-modal-mount-point-widget`;

	widgetSpanElement.contentEditable = 'false';

	return Decoration.widget(pos, widgetSpanElement, {
		side: 1,
		key: 'modalMountPointDecoration',
	});
};

export const createInlineDecoration = (from: number, to: number): Decoration => {
	return Decoration.inline(
		from,
		to,
		{ class: 'ai-selection-highlight', 'data-testid': 'ai-selection-highlight' },
		{ key: 'inlineDecoration' },
	);
};

export const createGeneratedContentDecorations = (
	from: number,
	to: number,
	isInDocumentStreamingEnabled?: boolean,
): Decoration[] => {
	const startWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		from,
		'start',
		'generatedStartWidget',
	);

	const highlightInlineDecoration = Decoration.inline(
		from,
		to,
		{ class: 'ai-generated-content-highlight', 'data-testid': 'ai-generated-content-highlight' },
		{ key: 'generatedInlineDecoration' },
	);

	const endWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		to,
		'end',
		'generatedEndWidget',
	);

	return [startWidgetDecoration, highlightInlineDecoration, endWidgetDecoration];
};

export const createNodeDecoration = (from: number, to: number): Decoration => {
	return Decoration.node(
		from,
		to,
		{ class: 'ai-selection-node', 'data-testid': 'ai-selection-node' },
		{ key: 'ai-nodeDecoration' },
	);
};

export const createGeneratedNodeDecorations = (from: number, to: number): Decoration => {
	return Decoration.node(
		from,
		to,
		{ class: 'ai-generated-node-highlight', 'data-testid': 'ai-generated-node-highlight' },
		{ key: 'generatedNodeDecoration' },
	);
};

function getInitialPositions(selection: Selection): DecorationPositions {
	switch (true) {
		case selection instanceof AllSelection:
			return { startPos: selection.from + 1, endPos: selection.to - 1 };
		default:
			return { startPos: selection.from, endPos: selection.to };
	}
}

export const getModalDecorationsForNodeSelection = ({
	state,
}: Pick<OpenAIModalProps, 'state' | 'positions'>): Decoration[] => {
	const { selection } = state;

	let from: number | undefined, to: number | undefined;
	if (selection instanceof NodeSelection) {
		from = selection.from;
		to = selection.to;
	} else if (isCellSelection(state)) {
		const findResult = findParentNodeOfType(state.schema.nodes.table)(selection);
		if (findResult) {
			const { depth } = findResult;
			from = selection.$from.before(depth);
			to = selection.$from.after(depth);
		}
	} else {
		const { selection } = state;
		const supportedNodes = getSupportedNodesForAIModal(state);

		const findResult = findParentNode((node) => supportedNodes.has(node.type))(selection);
		if (findResult) {
			const { depth } = findResult;
			from = selection.$from.before(depth);
			to = selection.$from.after(depth);
		}
	}

	if (from !== undefined && to !== undefined) {
		return [createNodeDecoration(from, to)];
	}
	return [];
};

export const getModalDecorationsForRangeSelection = ({
	state,
	configItem,
	lastTriggeredFrom,
	initialPositions,
}: Omit<OpenAIModalProps, 'triggerMethod' | 'aiGlobalOptIn' | 'initialPrompt' | 'triggeredFor'> & {
	initialPositions: Positions;
}): Decoration[] => {
	/**
	 * We add selection highlight decoration based on the current selection state of the document and the config item in the command palette
	 * If the selection is empty and the document is not empty -> we decorate the entire document unless the config item specifies otherwise
	 */
	let useCurrentSelectionForHighlight = true;

	// when clicking `browse agents` then selecting an agent, the state.selection is already cleared
	// so we need to use initialPositions to check if the selection is empty
	if (initialPositions[0] === initialPositions[1] && state.doc.content.size !== 0) {
		useCurrentSelectionForHighlight = !!configItem.doNotHighlightDocWhenSelectionIsEmpty;
	}

	/**
	 * EXPERIMENTAL: For experimental flow where 'Suggest title' in
	 * Confluence title toolbar will trigger the 'Suggest a title' flow in Editor AI.
	 * Forces whole document selection, even if an existing selection exists.
	 */
	if (isConfluenceTitleToolbarSuggestTitle({ configItem, lastTriggeredFrom })) {
		useCurrentSelectionForHighlight = false;
	}

	const { startPos, endPos } = useCurrentSelectionForHighlight
		? { startPos: initialPositions[0], endPos: initialPositions[1] }
		: getInitialPositions(new AllSelection(state.doc));

	let decorations: Decoration[] = [];
	const { isInDocumentStreamingEnabled } = getPluginState(state);

	const startWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		initialPositions[0],
		'start',
	);
	const endWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		initialPositions[1],
		'end',
	);
	decorations =
		initialPositions[0] === initialPositions[1]
			? [endWidgetDecoration]
			: [startWidgetDecoration, endWidgetDecoration];
	decorations.push(createInlineDecoration(startPos, endPos));

	return decorations;
};

function createEmptySelectionDecoration(startPos: number) {
	const widgetSpanElement = document.createElement('span');
	widgetSpanElement.setAttribute('data-testid', 'ai-pre-stream-empty-selection-caret');
	widgetSpanElement.className = 'ai-pre-stream-empty-selection-caret';

	widgetSpanElement.contentEditable = 'false';
	return Decoration.widget(startPos, widgetSpanElement, {
		side: 1,
		key: 'ai-pre-stream-empty-selection-caret',
	});
}

export const getModalDecorations = ({
	state,
	configItem,
	initialPositions,
	skipSelectionHighlight = false,
}: {
	configItem: EditorPluginAIConfigItem;
	initialPositions: Positions;
	/**
	 * When true, omits the visible highlight decorations (range highlight and
	 * empty-selection caret) while still emitting the invisible mount-point
	 * decorations the modal needs to render and anchor.
	 */
	skipSelectionHighlight?: boolean;
	state: EditorState;
}): Decoration[] => {
	// We add selection highlight decoration based on the current selection of the document
	// when clicking `browse agents` then selecting an agent, the state.selection is cleared
	// so we need to use initialPositions to get the selection positions
	const startPos = initialPositions[0];
	const endPos = initialPositions[1];
	const isEmptySelection = startPos === endPos;
	const isSuggestTitle = isSuggestTitleForConfluence(configItem);
	const needSelectionDecoration = !isSuggestTitle;

	const decorations: Decoration[] = [];
	if (needSelectionDecoration) {
		if (isEmptySelection) {
			// Caret is purely visual — skip when highlight is disabled.
			if (!skipSelectionHighlight) {
				decorations.push(createEmptySelectionDecoration(startPos));
			}
		} else {
			// Only the inline decoration highlights text; the mount-point range
			// decoration below is invisible and always required as the anchor.
			if (!skipSelectionHighlight) {
				const preStreamRangeDecorations = Decoration.inline(
					startPos,
					endPos,
					{
						class: 'ai-pre-stream-range-selection',
						'data-testid': 'ai-pre-stream-range-selection',
					},
					{ key: 'ai-pre-stream-range-selection' },
				);
				decorations.push(preStreamRangeDecorations);
			}

			const mountPointRangeDecorations = Decoration.inline(
				startPos,
				endPos,
				{},
				{ key: 'modalMountPointRangeDecoration' },
			);

			decorations.push(mountPointRangeDecorations);
		}
	}

	/**
	 * Set the mount point of the widget to be directly after the endPos so that
	 * replace steps during streaming will not remove the decoration as
	 * that would unmount the modal + state machine
	 */
	const mountPos = isSuggestTitle
		? 1
		: isEmptySelection && !configItem.insertOnEmptySelection
			? state.doc.content.size
			: Math.min(endPos, state.doc.content.size);

	const mountPointDecoration = createModalMountPointDecoration(mountPos);
	decorations.push(mountPointDecoration);
	return decorations;
};

type OpenAIModalProps = {
	adjustSelection?: AdjustSelectionInstruction;
	aiGlobalOptIn: AIGlobalOptIn;
	/**
	 * Optional override for the `aiSessionContextPluginKey` meta type
	 * that is normally derived as `'PROMPT'` / `'STREAM'` from the config item.
	 *
	 * Used by `openPostStreamReviewModal` (New AIFC Editor Experience) so the
	 * modal opens directly into the Post Stream Review screen without flowing
	 * through the usual prompt / stream lifecycle. Also carries the extra
	 * payload (`currentStreamReviewSegmentIndex`, `aiOptIn`) needed by the
	 * `POST_STREAM_REVIEW` reducer case.
	 *
	 * When omitted, the original derivation is preserved.
	 */
	aiSessionContextType?:
		| { type: 'PROMPT' }
		| { type: 'STREAM' }
		| {
				aiOptIn?: AIGlobalOptIn;
				currentStreamReviewSegmentIndex: number;
				type: 'POST_STREAM_REVIEW';
				/**
				 * Optional Undo/Redo `isUndone` flip applied atomically with the modal
				 * open. Forwarded verbatim into the `POST_STREAM_REVIEW` meta so the
				 * slice-replace, modal re-open, and state change all commit in one
				 * transaction. See `PostStreamReviewAction.undoneUpdate`.
				 */
				undoneUpdate?: PostStreamReviewUndoneUpdate;
		  };
	/**
	 * Optional hook invoked at the **start** of the createCommand
	 * transform — before any of the modal-open meta / selection-clearing logic
	 * runs. Lets callers piggy-back domain-specific edits onto the same
	 * transaction that opens the modal, avoiding a second dispatch.
	 *
	 * Used by the Post Stream Review screen's Undo/Redo so the slice-replace
	 * and the re-issue of `SHOW_MODAL` (with a fresh decoration anchored at
	 * the new range) commit atomically. Without this, two dispatches would be
	 * needed and the intermediate state can briefly leave the modal anchored
	 * at a stale (or empty) decoration range.
	 *
	 * The callback should mutate `tr` in place and return it — same contract
	 * as the transform callback itself.
	 */
	beforeTransform?: (tr: Transaction) => Transaction;
	configItem: EditorPluginAIConfigItem;
	has3pSources?: boolean;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	initialPrompt?: string;
	isCreate?: boolean;
	isExternal?: boolean;
	lastTriggeredFrom?: InvokedFrom;
	/**
	 * This is required for expanding selection for AI experience.
	 * Currently it is optional because making it mandatory will require
	 * 	big changes across many files and change in public api as well.
	 * So we will currently fallback to "en-GB" if it is not provided.
	 */
	locale?: string;
	nudgeMetrics?: Record<string, number | undefined>;
	/**
	 * When both are defined, these positions override state.selection.from and
	 * state.selection.to respectively for determining the initial selection range.
	 */
	overrideFromPos?: number;
	overrideToPos?: number;
	positions?: DecorationPositions;
	promptVersion?: string;
	/**
	 * When false, skips all selection expansion (word boundaries, block nodes, etc.).
	 * Useful when the positions are already exact (e.g. paste actions).
	 * Defaults to true.
	 */
	shouldExpandPositions?: boolean;
	singleInstrumentationId?: string;
	/**
	 * When true, the modal is still decorated and anchored but the range text
	 * highlight is omitted. Used by the Post Stream Review modal. Defaults to false.
	 */
	skipSelectionHighlight?: boolean;
	sourceProduct?: string | null;
	state: EditorState;
	traceId?: string;
	triggeredFor?: NodeType;
	triggeredForMark?: MarkType;
	triggerMethod?: InvokedFromTriggerMethod;
	userADFInput?: JSONDocNode;
	willAISuggestEmoji?: boolean;
	withAcceptReject?: boolean;
	withStreamingHighlight?: boolean;
};

export const createOpenAIModalCommand = ({
	state,
	configItem,
	lastTriggeredFrom,
	triggerMethod,
	aiGlobalOptIn,
	aiSessionContextType,
	initialPrompt,
	triggeredFor,
	triggeredForMark,
	/**
	 * Nudge metrics will be used passed in analytics events.
	 * It includes detailed metrics for improve writing nudge.
	 * It is currently only set when "Improve writing" is triggered from the
	 * "Improve writing" nudge.
	 */
	nudgeMetrics,
	locale,
	isExternal = false,
	withAcceptReject = true,
	withStreamingHighlight = true,
	hideLoadingScreen = false,
	traceId,
	singleInstrumentationId,
	isCreate,
	overrideFromPos,
	overrideToPos,
	shouldExpandPositions = true,
	promptVersion,
	has3pSources,
	sourceProduct,
	userADFInput,
	willAISuggestEmoji,
	beforeTransform,
	skipSelectionHighlight = false,
}: OpenAIModalProps): Command => {
	/**
	 * WARNING: Do not remove the isFedRamp check.
	 * This is to ensure that AI functionality is not enabled in FedRamp environments.
	 */
	if (aiGlobalOptIn.status === 'disabled' || isFedRamp()) {
		/**
		 * If the user has opted out of AI, or if user is in FedRamp environment,
		 * do not show the modal it's unclear how this function would be triggered as
		 * all entry points should be removed already
		 */
		return () => false;
	}

	if (aiGlobalOptIn.status === 'disabled-opt-in') {
		aiGlobalOptIn.triggerOptInFlow();

		return createCommand({}, (tr: Transaction) => {
			addAnalytics({
				editorState: state,
				tr,
				payload: {
					action: 'triggerOptInFlow',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'aiGlobalOptIn',
					attributes: {
						experienceType: convertExperienceName(configItem.key),
						lastTriggeredFrom,
						selectionType: state.selection.empty ? 'empty' : 'range',
					},
					eventType: EVENT_TYPE.UI,
				},
			});
			return tr;
		});
	}

	const usePositionOverride = overrideFromPos !== undefined && overrideToPos !== undefined;
	const from = usePositionOverride ? overrideFromPos : state.selection.from;
	const to = usePositionOverride ? overrideToPos : state.selection.to;

	const aiDecorationExperiencePluginState = getPluginState(state);
	const decorationPluginStateInitialPositions = aiDecorationExperiencePluginState.initialPositions;

	// For external commands respect the current selection
	let initialPositions: Positions =
		isExternal && aiDecorationExperiencePluginState.isInDocumentStreamingEnabled
			? [from, to]
			: decorationPluginStateInitialPositions || [from, to];
	const adfStreamingEnabled = configItem.adfPrompt;
	const isSmartLinkNode =
		triggeredFor?.name === 'inlineCard' ||
		triggeredFor?.name === 'blockCard' ||
		triggeredFor?.name === 'embedCard';
	const isNativeEmbed =
		triggeredFor?.name === 'extension' &&
		state.selection instanceof NodeSelection &&
		state.selection.node.attrs?.extensionKey?.includes('native-embed') &&
		fg('platform_native_embeds_rollout_non_maui_experience');
	const isHyperlink =
		triggeredForMark?.name === 'link' &&
		expValEqualsNoExposure('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true);
	const isAnyLink = isSmartLinkNode || isHyperlink;
	const isBlockSmartLink =
		(triggeredFor?.name === 'blockCard' || triggeredFor?.name === 'embedCard') &&
		expValEqualsNoExposure('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true);

	// For smart link nodes, hyperlinks, and native embeds, we shift the initialPositions to after the parent block
	// (e.g. paragraph) containing the link, or after the block node itself.
	// This to make sure that the streaming content is inserted below the link/embed rather than replacing it.
	if (isNativeEmbed) {
		// extension nodes are block-level: selection.to already points after the node, use it directly.
		const afterNodePos = initialPositions[1];
		initialPositions = [afterNodePos, afterNodePos];
	} else if (
		isAnyLink &&
		expValEqualsNoExposure('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true)
	) {
		// blockCard/embedCard: position is already after the card; inlineCard/hyperlink: use .after() to move past the containing paragraph.
		// Depth 0 means we're already at the top level (e.g. pasted link resolved to an embed), so skip .after() to avoid "no position after the top-level node".
		const $endPos = state.doc.resolve(initialPositions[1]);
		const afterParagraphPos =
			isBlockSmartLink || $endPos.depth === 0 ? initialPositions[1] : $endPos.after();
		initialPositions = [afterParagraphPos, afterParagraphPos];
	}

	// No need to expand selection for block nodes, smart link nodes, or hyperlinks
	if (
		shouldExpandPositions &&
		!triggeredFor?.isBlock &&
		!isAnyLink &&
		(adfStreamingEnabled || aiDecorationExperiencePluginState.isInDocumentStreamingEnabled)
	) {
		// For external commands respect the current selection
		if (isExternal) {
			const [start, end] = expandSelectionForAIExperience({
				selectionPositions: { startPos: state.selection.$from, endPos: state.selection.$to },
				/**
				 * Currently locale is optional because making it mandatory will require
				 * 	big changes across many files and change in public api as well.
				 * So we will currently fallback to "en-GB" if it is not provided.
				 */
				context: {
					locale: locale ?? 'en-GB',
					selection: state.selection,
				},
				options: {
					shouldExpandSelectionToCompleteWords: true,
					shouldExpandSelectionToMediaIfInCaption: true,
					shouldExpandSelectionToNearestBlockNode: true,
					shouldTransformCellSelectionToTextRange: true,
				},
			});
			initialPositions = [start, end];
		} else {
			const selectionPositions = decorationPluginStateInitialPositions
				? {
						startPos: state.doc.resolve(decorationPluginStateInitialPositions[0]),
						endPos: state.doc.resolve(decorationPluginStateInitialPositions[1]),
					}
				: usePositionOverride && overrideFromPos !== undefined && overrideToPos !== undefined
					? {
							startPos: state.doc.resolve(overrideFromPos),
							endPos: state.doc.resolve(overrideToPos),
						}
					: { startPos: state.selection.$from, endPos: state.selection.$to };
			const [start, end] = expandSelectionForAIExperience({
				selectionPositions,
				/**
				 * Currently locale is optional because making it mandatory will require
				 * 	big changes across many files and change in public api as well.
				 * So we will currently fallback to "en-GB" if it is not provided.
				 */
				context: {
					locale: locale ?? 'en-GB',
					selection: state.selection,
				},
				options: {
					shouldExpandSelectionToCompleteWords: true,
					shouldExpandSelectionToMediaIfInCaption: true,
					shouldExpandSelectionToNearestBlockNode: true,
					shouldTransformCellSelectionToTextRange: true,
					shouldExpandSelectionToEntireLayout:
						aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
				},
			});
			initialPositions = [start, end];
		}
	}

	let modalDecorationSet = DecorationSet.empty;
	if (triggeredFor?.isBlock && !isBlockSmartLink && !isHyperlink && !isNativeEmbed) {
		// First get the positions that the decorations will be added to the document at
		const decorations = getModalDecorationsForNodeSelection({ state });
		if (decorations.length) {
			modalDecorationSet = modalDecorationSet.add(state.doc, decorations);
		}
	} else {
		// skipSelectionHighlight keeps the invisible mount-point decorations for
		// anchoring but drops the visible highlight (Post Stream Review modal).
		const decorations = aiDecorationExperiencePluginState.isInDocumentStreamingEnabled
			? getModalDecorations({ state, configItem, initialPositions, skipSelectionHighlight })
			: getModalDecorationsForRangeSelection({
					state,
					configItem,
					lastTriggeredFrom,
					initialPositions,
				});
		if (decorations.length) {
			modalDecorationSet = modalDecorationSet.add(state.doc, decorations);
		}
	}

	// This "command" is later picked up by a state reducer
	const action: ShowModalAction = {
		type: 'SHOW_MODAL',
		data: {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			interactionSessionId: uuid(),
			lastTriggeredFrom,
			triggerMethod,
			modalDecorationSet,
			configItem,
			modalMountedTimeStamp: Date.now(),
			initialPrompt,
			userADFInput,
			triggeredFor,
			triggeredForMark,
			initialPositions: initialPositions,
			initialSelection: aiDecorationExperiencePluginState.initialSelection || state.selection,
			nudgeMetrics,
			withAcceptReject,
			withStreamingHighlight,
			hideLoadingScreen,
			traceId,
			singleInstrumentationId,
			isCreate,
			promptVersion,
			has3pSources,
			sourceProduct,
			willAISuggestEmoji,
		},
	};

	if (aiDecorationExperiencePluginState.isInDocumentStreamingEnabled) {
		return createCommand(action, (tr: Transaction) => {
			// Run caller-supplied piggy-back edits first so any doc
			// changes are visible to subsequent logic in this transform (e.g. the
			// selection-clearing block below reads `tr.selection`).
			if (beforeTransform) {
				tr = beforeTransform(tr);
			}
			// This is a special use case where we want to clear the selection locally, but inform the collab users that a selection
			// range was selected. The useSelectionBookmarkForRemote will be sent to collab users instead of the local setSelection.
			// We don't want to have a selection range in the editor when we start replacing the content with AI generated content.
			// This is because the FocusLock will keep draging focus away which will break selection ranges.
			// See: https://hello.jira.atlassian.cloud/browse/EDITOR-1090 for more details.
			const selection = tr.selection;
			const { to, from } = selection;
			if (
				(!triggeredFor?.isBlock || isBlockSmartLink || isHyperlink || isNativeEmbed) &&
				to !== from
			) {
				tr.setSelection(TextSelection.create(tr.doc, to));
				// There will be weird selection highlight if we don't clear the selection manually
				// see https://hello.jira.atlassian.cloud/browse/EDITOR-1405
				const domSelection = window.getSelection();
				if (domSelection) {
					domSelection.removeAllRanges();
				}
			}

			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				// the orchestrator can send COMPLETE -> COMPLETE so we do this to reset the streaming state
				// and handle new content on the second COMPLETE
				//
				// When `aiSessionContextType` is provided (currently only by
				// `openPostStreamReviewModal`), use it verbatim so the SessionUI lands
				// directly in the Post Stream Review screen instead of the prompt / stream
				// loading flow.
				tr.setMeta(
					aiSessionContextPluginKey,
					aiSessionContextType ?? {
						type:
							(!isExternal && configItemUsesRovo(configItem.key)) ||
							// Extenal applications can use `openAIModal` without
							(configItemUsesFreeGen(configItem.key) && isExternal)
								? 'PROMPT'
								: 'STREAM',
					},
				);
			}
			// when ai orchestrator endStreamCommand timeout, we need endStream to reset streaming state
			let endStreamCommand = endStream;
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				endStreamCommand = purePmEndStream;
			}
			return endStreamCommand(tr)
				.setMeta(
					'useSelectionBookmarkForRemote',
					TextSelection.between(
						tr.doc.resolve(initialPositions[0]),
						tr.doc.resolve(initialPositions[1]),
					).getBookmark(),
				)
				.setMeta(selectionPluginKey, {
					action: 'lastSelectionContext',
					selection,
				})
				.setMeta('scrollIntoView', false);
		});
	} else {
		// Clear the selection after opening the modal to hide annotation toolbar
		return createCommand(action, (tr: Transaction) => {
			// See comment on the streaming branch above.
			if (beforeTransform) {
				tr = beforeTransform(tr);
			}
			const selection = tr.selection;
			const { to, from } = selection;
			if (
				(!triggeredFor?.isBlock || isBlockSmartLink || isHyperlink || isNativeEmbed) &&
				to !== from
			) {
				tr.setSelection(TextSelection.create(tr.doc, to));
			}
			// Same intent as streaming path, but only when Maui inline Rovo UX needs selectionContext
			// (cc-maui-experiment). Avoids persisting context for all non-streaming AI opens.
			if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				return tr.setMeta(selectionPluginKey, {
					action: 'lastSelectionContext',
					selection,
				});
			}
			return tr;
		});
	}
};

export const createEndAIExperienceCommand = (
	modalDecorationSet?: DecorationSet,
	preserveEditorSelectionOnComplete?: boolean,
	shouldClearSelectionContext?: boolean,
	shouldIgnoreNextSpaceTrigger?: boolean,
): Command => {
	return createCommand(
		(state: EditorState) => {
			if (modalDecorationSet && !preserveEditorSelectionOnComplete) {
				return {
					type: 'END_EXPERIENCE',
					data: {
						modalDecorationSet,
						ignoredSpaceTriggerSelection: shouldIgnoreNextSpaceTrigger
							? state.selection
							: undefined,
					},
				};
			}
			return {
				type: 'END_EXPERIENCE',
				data: {
					ignoredSpaceTriggerSelection: shouldIgnoreNextSpaceTrigger ? state.selection : undefined,
				},
			};
		},
		(tr: Transaction, state: EditorState) => {
			if (preserveEditorSelectionOnComplete) {
				const inlineDeco = modalDecorationSet?.find(
					undefined,
					undefined,
					(spec) => spec.key === 'inlineDecoration',
				)?.[0];
				if (inlineDeco) {
					tr.setSelection(
						TextSelection.create(
							tr.doc,
							inlineDeco.from,
							Math.min(inlineDeco.to, tr.doc.content.size),
						),
					);
				}
			}

			const aiDecorationExperiencePluginState = getPluginState(state);
			if (aiDecorationExperiencePluginState.isInDocumentStreamingEnabled) {
				if (shouldClearSelectionContext === false) {
					tr.setMeta(selectionPluginKey, {
						action: 'lastSelectionContextPending',
					});
				} else {
					tr.setMeta(selectionPluginKey, {
						action: 'lastSelectionContext',
						selection: undefined,
					});
				}

				let endStreamCommand = endStream;
				if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
					endStreamCommand = purePmEndStream;
				}
				return endStreamCommand(endWatchingAnnotationChanges(tr));
			}

			return tr;
		},
	);
};

export const clearSelectionDecorations = (state: EditorState): Command => {
	const aiDecorationExperiencePluginState = getPluginState(state);
	const modalDecorationSet = aiDecorationExperiencePluginState.modalDecorationSet;
	const selectionDecorations = modalDecorationSet.find(
		undefined,
		undefined,
		(spec) =>
			spec.key === 'ai-pre-stream-range-selection' ||
			spec.key === 'ai-pre-stream-empty-selection-caret',
	);

	return createCommand({
		type: 'UPDATE_MODAL_DECORATION_SET',
		data: { modalDecorationSet: modalDecorationSet.remove(selectionDecorations) },
	});
};

export const createUpdateEndWidgetDecorationPositionCommand = (
	state: EditorState,
	endWidgetDecorationPosition: number,
): Command => {
	const aiDecorationExperiencePluginState = getPluginState(state);
	const modalDecorationSet = aiDecorationExperiencePluginState.modalDecorationSet;
	const decorationWidget = modalDecorationSet.find(
		undefined,
		undefined,
		(spec) => spec.key === 'endWidgetDecoration',
	);
	let updated = modalDecorationSet.remove(decorationWidget);
	const updatedEndWidgetDecoration = createWidgetDecoration(
		aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
	)(endWidgetDecorationPosition, 'end');
	updated = updated.add(state.doc, [updatedEndWidgetDecoration]);
	return createCommand({
		type: 'UPDATE_MODAL_DECORATION_SET',
		data: { modalDecorationSet: updated },
	});
};

export const createStartAISessionCommand = (
	sessionId: string,
	traceId?: string,
	promptVersion?: string,
	agentInfo?: AgentInfo,
): Command => {
	return createCommand(
		{
			type: 'START_SESSION',
			data: {
				sessionId,
				traceId: traceId,
				promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
					? promptVersion
					: undefined,
				agentInfo,
			},
		},
		(tr: Transaction) => {
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				tr.setMeta(aiSessionContextPluginKey, {
					type: 'START_SESSION',
					sessionId,
				});
			}

			return tr;
		},
	);
};

export const createEndAISessionCommand = (sessionId: string): Command => {
	return createCommand((state) => {
		const aiDecorationExperiencePluginState = getPluginState(state);
		const nextSessionId = aiDecorationExperiencePluginState.nextSessionId;

		// If a sessions which is not the next session is trying to be ended then we should abort trying to end the session.
		if (sessionId !== nextSessionId) {
			return false;
		}

		return {
			type: 'END_SESSION',
			data: { sessionId: undefined },
		};
	});
};

export const createUpdateTargetIdCommand = (targetId: string | number): Command => {
	return createCommand({
		type: 'UPDATE_TARGET_ID',
		data: { targetId },
	});
};

/**
 * Clear the inline Rovo streaming target. Call on completion/abort —
 * NOT on component unmount (fires multiple times per invocation).
 */
export const createEndInlineRovoStreamingCommand = (): Command => {
	return createCommand({
		type: 'END_INLINE_ROVO_STREAMING',
	});
};

/** Stash a candidate target id without triggering the loading overlay. */
export const createStageInlineRovoStreamingCommand = (targetId: string): Command => {
	return createCommand({
		type: 'STAGE_INLINE_ROVO_STREAMING',
		data: { targetId },
	});
};

/** Promote the staged target id to the live value. No-op if nothing is staged. */
export const createCommitInlineRovoStreamingCommand = (): Command => {
	return createCommand({
		type: 'COMMIT_INLINE_ROVO_STREAMING',
	});
};

/** Show the floating inline-edit "ready" bar after completion (visual only). */
export const createShowInlineEditReadyCommand = (): Command => {
	return createCommand({
		type: 'SHOW_INLINE_EDIT_READY',
	});
};

/** Dismiss the floating inline-edit "ready" bar. */
export const createDismissInlineEditReadyCommand = (): Command => {
	return createCommand({
		type: 'DISMISS_INLINE_EDIT_READY',
	});
};
